import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Grid,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';

import useAdminRequest from '../../../requests/AdminRequest';
import Config from '../../../Config';
import CustomModal from '../../common/CustomModal';
import Loader from '../../common/Loader';
import Paginator from '../../common/Paginator';

const AuthLog = (props) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [showMessages, setShowMessages] = useState(false);
  const [messages, setMessages] = useState([]);
  const [onDate, setOnDate] = useState(new Date());
  const [selectedEmail, setSelectedEmail] = useState('');
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesPage, setMessagesPage] = useState(1);
  const [messagesCount, setMessagesCount] = useState(0);
  const { getAuthGroups, getAuthLogs } = useAdminRequest();

  const onRefresh = async () => {
    setLoading(true);
    const data = await getAuthGroups(
      onDate,
      page,
      Config.defaultPL,
      props.errorHandler
    );
    data.items.forEach((item) => {
      item.success_count = item.message_count - item.failure_count;
    });
    setData(data.items);
    setCount(data.total);
    setLoading(false);
  };

  const refreshMessages = async () => {
    if (!selectedEmail) return;

    setMessagesLoading(true);
    const data = await getAuthLogs(
      selectedEmail,
      onDate,
      messagesPage,
      Config.defaultPL,
      props.errorHandler
    );
    setMessages(data.items);
    setMessagesCount(data.total);
    setMessagesLoading(false);
  };

  const toggleDetails = async (item) => {
    setSelectedEmail(item.customer_user_email);
    setMessages([]);
    setMessagesPage(1);
    setShowMessages(true);
  };

  useEffect(() => {
    onRefresh();
  }, [page, onDate]);

  useEffect(() => {
    refreshMessages();
  }, [messagesPage, selectedEmail]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 2,
        }}
      >
        <Typography variant="h3">Auth Log</Typography>

        <Button
          variant="contained"
          color="secondary"
          onClick={() => onRefresh()}
        >
          Refresh
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ marginBottom: 2.25 }}>
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label="Since"
              value={moment(onDate)}
              onChange={(value) => setOnDate(value)}
              format="DD/MMM/YYYY"
              disableFuture
              fullWidth
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      {loading ? (
        <Loader />
      ) : (
        <>
          <TableContainer sx={{ marginBottom: 4, overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Last Attempt</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Counts</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3}>No data found</TableCell>
                  </TableRow>
                )}
                {data.map((item, index) => {
                  return (
                    <TableRow
                      key={index}
                      onClick={() => toggleDetails(item)}
                      hover
                    >
                      <TableCell>
                        {new Intl.DateTimeFormat('en-us', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: 'numeric',
                          second: 'numeric',
                        }).format(new Date(item.last_updated))}
                      </TableCell>
                      <TableCell>{item.customer_user_email}</TableCell>
                      <TableCell>
                        <span className="count-success" title="Success">
                          {item.success_count}
                        </span>
                        &nbsp;-&nbsp;
                        <span className="count-failed" title="Failed">
                          {item.failure_count}
                        </span>
                        &nbsp;/&nbsp;
                        <span title="Total">{item.message_count}</span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {count > 10 && (
            <Paginator
              itemCount={count}
              pageNumber={page}
              changeEvent={(page) => {
                setPage(page + 1);
              }}
            />
          )}
        </>
      )}

      <CustomModal
        open={showMessages}
        setOpen={setShowMessages}
        onClose={() => setShowMessages(false)}
        width={1300}
        title="Messages"
      >
        <TableContainer sx={{ marginBottom: 4, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Result</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messagesLoading && (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Loader />
                  </TableCell>
                </TableRow>
              )}
              {messages.map((message, index) => {
                const final = message.logs.find(
                  (log) => log.tag === 'AUTH_FINAL'
                );
                const result =
                  final ||
                  message.logs.find((log) => log.tag === 'AUTH_DETAILS');
                const clazz = result.log_message.toLowerCase().includes('fail')
                  ? 'text-danger'
                  : '';

                return (
                  <TableRow key={index}>
                    <TableCell>
                      {new Intl.DateTimeFormat('en-us', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                        second: 'numeric',
                      }).format(new Date(result.log_time))}
                    </TableCell>
                    <TableCell>
                      <span className={clazz}>{result.log_message}</span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {messagesCount > 10 && (
          <Paginator
            itemCount={messagesCount}
            changeEvent={(page) => {
              setMessagesPage(page);
            }}
          />
        )}
        <Box sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button onClick={() => setShowMessages(false)} color="secondary">
            Close
          </Button>
        </Box>
      </CustomModal>
    </>
  );
};

export default AuthLog;
